import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private timeoutMs = 2 * 60 * 1000; // default 2 minutes
  private warnMs = 30 * 1000; // default 30s warning
  private timeoutSub?: Subscription;
  private warningSub?: Subscription;
  private eventsBound = false;
  private onTimeoutSubject = new Subject<void>();
  public readonly onTimeout$ = this.onTimeoutSubject.asObservable();
  private onWarningSubject = new Subject<void>();
  public readonly onWarning$ = this.onWarningSubject.asObservable();

  private readonly activityEvents = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click'
  ];

  constructor(private zone: NgZone) {}

  init(timeoutMs?: number, warnMs?: number) {
    if (timeoutMs && timeoutMs > 0) this.timeoutMs = timeoutMs;
    if (warnMs && warnMs > 0 && warnMs < this.timeoutMs) this.warnMs = warnMs;
    this.bindActivityEvents();
    this.resetTimer();
  }

  resetTimer() {
    // Run timer outside Angular to avoid change detection churn
    this.zone.runOutsideAngular(() => {
      if (this.timeoutSub) {
        this.timeoutSub.unsubscribe();
      }
      if (this.warningSub) {
        this.warningSub.unsubscribe();
      }
      // schedule warning first (timeout - warnMs)
      const warnDelay = Math.max(0, this.timeoutMs - this.warnMs);
      this.warningSub = timer(warnDelay).subscribe(() => {
        this.zone.run(() => this.onWarningSubject.next());
      });
      this.timeoutSub = timer(this.timeoutMs).subscribe(() => {
        // Back to Angular zone to emit and let components handle logout
        this.zone.run(() => this.onTimeoutSubject.next());
      });
    });
  }

  stop() {
    if (this.timeoutSub) {
      this.timeoutSub.unsubscribe();
      this.timeoutSub = undefined;
    }
    if (this.warningSub) {
      this.warningSub.unsubscribe();
      this.warningSub = undefined;
    }
    this.unbindActivityEvents();
  }

  private handleActivity = () => this.resetTimer();

  private bindActivityEvents() {
    if (this.eventsBound) return;
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach(evt => window.addEventListener(evt, this.handleActivity, { passive: true }));
      this.eventsBound = true;
    });
  }

  private unbindActivityEvents() {
    if (!this.eventsBound) return;
    this.activityEvents.forEach(evt => window.removeEventListener(evt, this.handleActivity));
    this.eventsBound = false;
  }

  ngOnDestroy(): void {
    this.stop();
    this.onTimeoutSubject.complete();
    this.onWarningSubject.complete();
  }
}
